import Immutable from "immutable";
import _ from "lodash";
import platform from "platform";
import posthog from "posthog-js";
import Reflux from "reflux";
import { isIPhone, isIpad, isSupportedIpad, isTablet } from "@/constants/BrowserConstants";
import AppActions from "../actions/AppActions";
import UIActions from "../actions/UIActions";
import UserActions from "../actions/UserActions";
import config from "../config/app";
import AppConstants from "../constants/AppConstants";
import { FORM_STATE_DEFAULT } from "@/constants/FormFieldConstants.mjs";
import { BOOL, OBJECT } from "../constants/LocalStorageMap";
import AppService from "../service/AppService";
import LocalStorageService from "../service/LocalStorageService";
import { formattedFromNow, isAfterNow } from "../utils/DateUtils";
import {
  createRepeatedExceptionFilter,
  dropUnactionableExceptions,
} from "../utils/errorTrackingFilter";
import { getInitialRemFromLocalStorage } from "../utils/UIUtils";
import { persistParams } from "../utils/UrlUtils";

const { BUTTON_STATE_REGULAR } = AppConstants;
const TIME_BETWEEN_ACCOUNT_CREATION_MODAL_OPEN = 120000; // 2 minutes

const initializeIntercom = (intercomAppId) => {
  var w = window;
  var ic = w.Intercom;
  if (typeof ic === "function") {
    ic("update", w.intercomSettings);
  } else {
    var d = document;
    var i = (...args) => {
      i.c(args);
    };
    i.q = [];
    i.c = (args) => {
      i.q.push(args);
    };
    w.Intercom = i;
    var l = () => {
      var s = d.createElement("script");
      s.type = "text/javascript";
      s.async = true;
      s.src = "https://widget.intercom.io/widget/" + intercomAppId;
      var x = d.getElementsByTagName("script")[0];
      x.parentNode.insertBefore(s, x);
    };
    if (document.readyState === "complete") {
      l();
    } else if (w.attachEvent) {
      w.attachEvent("onload", l);
    } else {
      w.addEventListener("load", l, false);
    }
  }
};

const initializeGTM = (gtmCode) => {
  ((w, d, s, l, i) => {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l != "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", gtmCode);
};

const AppStore = Reflux.createStore({
  storeName: "AppStore",
  listenables: [UIActions, AppActions],

  init() {
    this.listenTo(UserActions.logout, this.onShowLogin);

    this.previousViews = [];

    this.state = Immutable.fromJS({
      buttonState: BUTTON_STATE_REGULAR,
      formFieldState: FORM_STATE_DEFAULT,
      mainView: AppConstants.MAIN_VIEW_LOADING,
      mainViewProps: null,
      savingError: false,
      initializing: false,
      initialized: false,
      appError: false,

      modals: {
        designImport: {
          state: AppConstants.MODAL_STATE_INACTIVE,
        },
        actionConfirm: {
          state: AppConstants.MODAL_STATE_INACTIVE,
          overlay: true,
        },
      },
    });
  },
  onInitialize(init) {
    config.api = init.api;
    config.ver = init.ver;
    config.intercom = _.get(init, "intercom", false);
    config.satismeter = _.get(init, "satismeter", false) || false;
    config.googleFontsUrl = init.googleAPI
      ? `${"https://www.googleapis.com/webfonts/v1/webfonts?key="}${
          init.googleAPI
        }`
      : false;
    config.devMode = _.get(init, "devMode", false);
    config.instagramMediaUrl = _.get(init, "instagramMediaUrl", false);
    config.demoMode = _.get(init, "demoMode", false);
    config.posthog = _.get(init, "posthog", false);
    config.posthogAPIHost = _.get(init, "posthogAPIHost");
    config.adminURL = _.get(init, "admin", config.api.replace("api", "mc"));
    config.accountURL = _.get(init, "account");
    config.gtmCode = _.get(init, "gtmCode", false);
    config.dataModelVersion = init?.dataModelVersion;
    config.awsURL = init?.awsURL || "//s3-external-1.amazonaws.com";
    config.libraryURL = init?.libraryURL || "//lib.showit.com";
    config.protorenderer = init?.protorenderer;
    config.engineVersion = init?.engineVersion || null;

    if (config.intercom) {
      initializeIntercom(config.intercom);
    }

    if (config.posthog) {
      posthog.init(config.posthog, {
        api_host: config.posthogAPIHost,
        ui_host: "https://us.posthog.com",
        autocapture: false,
        before_send: [dropUnactionableExceptions, createRepeatedExceptionFilter()],
      });
      // PostHog reads the device from the user agent, and an iPad asking for
      // desktop sites reports a Mac, so every event says whether it's a tablet
      posthog.register({ is_tablet: isTablet });
    }

    if (config.gtmCode) {
      initializeGTM(config.gtmCode);
    }

    if (config.accountURL) {
      config.lostPasswordURL = `${config.accountURL}/password/forgot`;
      config.createAccountURL = `${config.accountURL}/signup`;
    }

    const initialRem = getInitialRemFromLocalStorage();
    if (initialRem) {
      UIActions.setUIScale(initialRem);
    }

    var debugJSON = LocalStorageService.get(
      AppConstants.LOCAL_STORE_DEBUG_JSON_KEY,
      BOOL
    );
    config.debugJSON = debugJSON ? true : false;

    const browser = platform.name.toLowerCase();
    const browserOverrides = LocalStorageService.get(AppConstants.LOCAL_STORE_BROWSER_OVERRIDE, OBJECT);
    const minVersion = AppConstants.BROWSERS[browser] ?? browserOverrides[browser];
    const browserVersion = Number(platform.version.split(".")[0]);
    // phones aren't supported in any browser
    const isKnownBrowser = Boolean(minVersion) && !isIPhone;
    const supported = isSupportedIpad || (isKnownBrowser && browserVersion >= minVersion);

    if (!supported) {
      config.reasonNotSupported = isIpad || isKnownBrowser ? "version" : "browser";
    }
    config.browserVer = browserVersion;
    config.browser = browser;
    config.isOSX = platform.os.family.toLowerCase() === "os x";

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("view")) {
      config.initView = urlParams.get("view");
    }

    if (urlParams.has("admin_token")) {
      localStorage.setItem(
        AppConstants.TOKEN_KEY,
        urlParams.get("admin_token")
      );
      localStorage.removeItem(AppConstants.SITE_KEY);
    }

    if (urlParams.has("site_design_id")) {
      if (config.demoMode) {
        config.demoModeSiteDesignId = urlParams.get("site_design_id");
      } else {
        localStorage.setItem(
          AppConstants.SITE_KEY,
          JSON.stringify({ id: _.toNumber(urlParams.get("site_design_id")) })
        );
      }
    }

    if (urlParams.has("init_design")) {
      localStorage.setItem(
        AppConstants.INIT_DESIGN,
        urlParams.get("init_design")
      );
    }

    const { protocol, host, pathname } = location;
    // preserve persistent query params
    const validParams = persistParams(urlParams);
    const paramString = validParams.toString();

    const clean_uri = `${protocol}//${host}${pathname}${
      paramString ? `?${paramString}` : ""
    }`;
    window.history.replaceState({}, document.title, clean_uri);

    if (supported) {
      if (config.demoMode) {
        UserActions.login({
          email: "testone@localhost.com",
          password: "password",
        });
      } else {
        UserActions.initialize();
      }
    } else {
      this._setMainView(AppConstants.MAIN_VIEW_BROWSER);
      this.trigger(this.state);
    }

    if (config.demoMode) {
      setInterval(() => {
        UIActions.openSignupModal();
      }, TIME_BETWEEN_ACCOUNT_CREATION_MODAL_OPEN);
    }

    AppActions.initializeFinished();
  },

  onModalOpen(modalName, data) {
    this._setModalState(modalName, data, AppConstants.MODAL_STATE_OPEN);
  },

  onModalClose(modalName) {
    this._setModalState(modalName, false, AppConstants.MODAL_STATE_CLOSED);
  },

  onModalDeactivate(modalName) {
    this._setModalState(modalName, false, AppConstants.MODAL_STATE_INACTIVE);
  },

  _setModalState(modalName, data, state) {
    var modal = this.state.getIn(["modals", modalName], false);
    if (modal) {
      modal = modal.set("state", state);
      modal = modal.set("data", data);

      this.state = this.state.setIn(["modals", modalName], modal);

      this.trigger(this.state);
    }
  },

  /**
   * @typedef {("regular"|"hover")} ButtonState
   */

  /**
   * @param {ButtonState} state
   */
  onSetButtonState(state) {
    this.state = this.state.set("buttonState", state);
    this.trigger(this.state);
  },

  /**
   * Sets which state the form field panel's tab shows; the stage previews it on the selected field.
   * @param {import("@/constants/FormFieldConstants.mjs").FormState} fieldState
   */
  onSetFormFieldState(fieldState) {
    this.state = this.state.set("formFieldState", fieldState);
    this.trigger(this.state);
  },

  onShowAppView(data) {
    if (data.allowBack) {
      this.previousViews.push(this.state.get("mainView"));
    }

    this._setMainView(data.view);

    if (_.has(data, "props")) {
      this.state = this.state.set("mainViewProps", data.props);
    }

    this.trigger(this.state);
  },

  onShowLogin() {
    this.previousViews = [];
    this._setMainView(AppConstants.MAIN_VIEW_LOGIN);
    this.trigger(this.state);
  },

  onShowLibrarySites(data) {
    if (data.allowBack) {
      this.previousViews.push(this.state.get("mainView"));
    }

    this._setMainView(AppConstants.MAIN_VIEW_LIBRARY_SITE_CHOOSER);
    this.trigger(this.state);
  },

  onShowOffline() {
    this._setMainView(AppConstants.MAIN_VIEW_OFFLINE);
    this.trigger(this.state);
  },

  onShowDesignView() {
    this.previousViews = [];
    this._setMainView(AppConstants.MAIN_VIEW_DESIGN);
    this.trigger(this.state);
  },

  onShowPreviousView() {
    if (this.previousViews.length > 0) {
      this._setMainView(this.previousViews.pop());
      this.trigger(this.state);
    }
  },

  onShowSaveError(error) {
    this.state = this.state.set("savingError", error);
    this.trigger(this.state);
  },

  onShowAppError(error) {
    this._setMainView(AppConstants.MAIN_VIEW_ERROR);
    this.state = this.state.set("appError", error);

    this.trigger(this.state);
  },

  onCheckAppStatus() {
    AppService.getInfo()
      .then(this._statusCheckComplete.bind(this))
      .catch(this._statusCheckError.bind(this));
  },

  _setMainView(view) {
    this.state = this.state.set("mainView", view);
    if (view == AppConstants.MAIN_VIEW_DESIGN) {
      UserActions.launchTour();
    }
  },

  /**
   * @typedef {Object} AppStatusResponse
   * @property {Object} body
   * @property {string} body.ver - Version number
   * @property {boolean} body.notifyUpdate - Whether to notify user of updates
   * @property {string} [body.notificationDescription] - Description of the update
   * @property {boolean} body.maintenanceMode - Whether app is in maintenance mode
   * @property {string} [body.maintenanceTime] - Timestamp for scheduled maintenance as unix timestamp in seconds
   * @property {boolean} body.forceRefresh - Whether to force a page refresh
   */

  /**
   * @param {AppStatusResponse} response
   */
  _statusCheckComplete(response) {
    const {
      ver: version,
      notifyUpdate,
      notificationDescription = "",
      maintenanceMode,
      maintenanceTime,
      forceRefresh,
    } = response?.body || {};
    if (maintenanceMode) {
      window.location.reload(true);
    } else if (maintenanceTime) {
      const dialogConfig = this._getMaintenancePendingConfig(maintenanceTime);

      const isOpen = this.state.getIn(["modals", "actionConfirm"], false);

      if (isOpen !== AppConstants.MODAL_STATE_OPEN) {
        UIActions.modalOpen(AppConstants.MODAL_ACTION_CONFIRM, dialogConfig);
      }
    } else if (version !== config.ver && notifyUpdate) {
      if (forceRefresh) {
        this._onReload();
      }
      const isOpen = this.state.getIn(["modals", "actionConfirm"], false);

      if (isOpen !== AppConstants.MODAL_STATE_OPEN) {
        const dialogConfig = {
          title: "Showit Updated",
          question: "We just rolled out some improvements to Showit!",
          message: notificationDescription,
          confirmBtn: "Reload",
          confirmAction: this._onReload,
          cancelBtn: "Remind me in 2 minutes",
        };

        UIActions.modalOpen(AppConstants.MODAL_ACTION_CONFIRM, dialogConfig);
      }
    }
  },

  _getMaintenancePendingConfig(/** Object **/ maintenanceTime) {
    const maintenanceIsPending = isAfterNow(maintenanceTime);

    const config = {
      title: "Showit Maintenance",
      question: "Showit is in maintenance mode",
      message: "Changes may not save correctly.  Please close this tab.",
      confirmBtn: "Ok",
      alertMode: true,
    };

    if (maintenanceIsPending) {
      const { formatted: formattedTimeFromNow } =
        formattedFromNow(maintenanceTime);
      config.question = `Showit will be undergoing maintenance ${formattedTimeFromNow}.`;
      config.message = "Please finish making any changes at this time.";
      config.confirmBtn = "Ok";
    }

    return config;
  },

  _onReload() {
    location.reload();
  },

  _statusCheckError(err) {
    //Unsure what to do on status check error
  },

  hasPreviousView() {
    return this.previousViews.length > 0;
  },

  getState() {
    return this.state;
  },

  getButtonState() {
    return this.state.get("buttonState");
  },

  /** @returns {import("@/constants/FormFieldConstants.mjs").FormState} */
  getFormFieldState() {
    return this.state.get("formFieldState");
  },

  setState(state) {
    this.state = state;

    this.trigger(this.state);
  },
});

export default AppStore;
